/**
 * CTO/Data Science review registry.
 *
 * Read-only planning surface for quant recipe research and recurring review.
 * This module intentionally has no Convex calls, no mutations, and no trading
 * configuration. It exists so research surfaces do not get forgotten after they
 * ship.
 */

export type ReviewCadence = "weekly" | "monthly";
export type ReviewStatus = "active" | "collecting" | "blocked" | "watch_only";

export interface ReviewSurface {
  id: string;
  name: string;
  ownerTab: string;
  status: ReviewStatus;
  cadence: ReviewCadence;
  purpose: string;
  nextReviewQuestion: string;
}

export interface RecipeDimension {
  id: string;
  label: string;
  examples: string[];
}

export interface RecipeSignal {
  agent?: string;
  symbol?: string;
  session?: string;
  outcome?: string;
  pnl_dollars?: number;
  hmm_regime?: string;
  kronos_direction?: string;
  sma50_aligned?: boolean | null;
  institutional_direction?: string;
  fbd_flow_score?: number;
  mr_flow_score?: number;
  forecast_stable_state?: string;
}

export interface RecipeMatrixRow {
  recipe: string;
  agent: string;
  n: number;
  wins: number;
  losses: number;
  winRate: number | null;
  totalPnl: number;
  avgPnl: number | null;
  symbols: string[];
  sessions: string[];
  robustness: "fragile" | "mixed" | "broad";
  status: "collect" | "discussion" | "manual_review";
}

export interface AgentSignalFrequencyRow {
  agent: string;
  n: number;
  share: number | null;
  sessions: string[];
  status: "starved" | "thin_sample" | "collecting";
  recommendation: string;
}

interface RecipeBucket extends RecipeMatrixRow {
  symbolSet: Set<string>;
  sessionSet: Set<string>;
}

export const RECIPE_DIMENSIONS: readonly RecipeDimension[] = [
  { id: "agent", label: "Agent / setup", examples: ["ORB long", "FBD reclaim", "FBD absorption", "MR short"] },
  { id: "context", label: "Market context", examples: ["symbol", "session", "HMM regime", "ADX regime"] },
  { id: "forecast", label: "Forecast stack", examples: ["FBE state", "Kronos D/4H/1H", "HMM D/4H/1H"] },
  { id: "trend", label: "Trend alignment", examples: ["SMA-50", "EMA-50", "VIX regime"] },
  { id: "flow", label: "Flow confirmation", examples: ["order flow", "volume flow", "trap score"] },
  { id: "outcome", label: "Outcome proof", examples: ["WR", "PnL", "Brier", "drawdown", "5/15/30/60m"] },
] as const;

export const REVIEW_SURFACES: readonly ReviewSurface[] = [
  {
    id: "accountability-tabs",
    name: "Accountability tabs",
    ownerTab: "Accountability",
    status: "active",
    cadence: "weekly",
    purpose: "Track ORB/FBD/MR/FBE/execution/regime/data evidence separately from live trading decisions.",
    nextReviewQuestion: "Which cohorts crossed 30/50 sample thresholds and still require manual review before promotion?",
  },
  {
    id: "fbe-layer-attribution",
    name: "FBE layer attribution",
    ownerTab: "Accountability → FBE",
    status: "collecting",
    cadence: "weekly",
    purpose: "Explain FBE votes by input layer freshness, vote direction, and contribution.",
    nextReviewQuestion: "Do newly-created FBE rows have 50/50 attribution coverage, and which layers caused misses?",
  },
  {
    id: "fbe-forecast",
    name: "FBE Forecast tab",
    ownerTab: "FBE Forecast",
    status: "active",
    cadence: "weekly",
    purpose: "Keep rolling/session FBE forecast interpretation away from trade execution.",
    nextReviewQuestion: "Is production FBE calibrated by symbol/session, and is candidate FBE improving Brier score shadow-only?",
  },
  {
    id: "markov-regime",
    name: "Markov Regime tab",
    ownerTab: "Markov Regime",
    status: "watch_only",
    cadence: "monthly",
    purpose: "Review regime-state behavior without changing live gates directly.",
    nextReviewQuestion: "Do Markov states line up with realized trend/range outcomes across complete forward samples?",
  },
  {
    id: "promotion-governance",
    name: "Promotion governance labels",
    ownerTab: "Accountability",
    status: "active",
    cadence: "weekly",
    purpose: "Make readiness/action labels visible and conservative: collect, review, keep LOG-ONLY, do not promote.",
    nextReviewQuestion: "Did any label become stale relative to new evidence or explicit Sabastian decisions?",
  },
  {
    id: "recipe-matrix",
    name: "Recipe Attribution Matrix",
    ownerTab: "CTO Review",
    status: "collecting",
    cadence: "weekly",
    purpose: "Find robust combinations of agent + forecast + regime + trend + flow that improve accuracy without overfit.",
    nextReviewQuestion: "Which recipes have 30+ discussion samples or 50+ manual-review samples across more than one symbol/session?",
  },
  {
    id: "fbd-mr-scarcity",
    name: "FBD/MR scarcity monitor",
    ownerTab: "CTO Review",
    status: "collecting",
    cadence: "weekly",
    purpose: "Quantify whether FBD and MR are genuinely under-sending relative to ORB before loosening gates or adding subscriber-facing watches.",
    nextReviewQuestion: "Are FBD/MR starved because valid setups are rare, or because near-misses are being hidden behind hard gates?",
  },
] as const;

export function getReviewSurfacesByCadence(cadence: ReviewCadence): ReviewSurface[] {
  return REVIEW_SURFACES.filter((surface) => surface.cadence === cadence);
}

export function summarizeReviewStatus(surfaces: readonly ReviewSurface[] = REVIEW_SURFACES): Record<ReviewStatus, number> {
  return surfaces.reduce<Record<ReviewStatus, number>>((acc, surface) => {
    acc[surface.status] += 1;
    return acc;
  }, { active: 0, collecting: 0, blocked: 0, watch_only: 0 });
}

export function recipeMatrixAcceptanceRule(): string {
  return "30+ samples = discussion only; 50+ forward samples with positive expectancy and no hidden symbol/session fragility = manual review; never auto-promote.";
}

function token(value: unknown, fallback = "missing"): string {
  if (typeof value === "string" && value.trim()) return value.trim().toLowerCase();
  if (typeof value === "boolean") return value ? "yes" : "no";
  return fallback;
}

function flowToken(signal: RecipeSignal): string {
  if (typeof signal.institutional_direction === "string" && signal.institutional_direction.trim()) {
    return signal.institutional_direction.trim().toLowerCase();
  }
  const flowScore = typeof signal.fbd_flow_score === "number" && Number.isFinite(signal.fbd_flow_score)
    ? signal.fbd_flow_score
    : typeof signal.mr_flow_score === "number" && Number.isFinite(signal.mr_flow_score)
      ? signal.mr_flow_score
      : null;
  if (flowScore === null) return "flow_missing";
  if (flowScore >= 0.6) return "flow_high";
  if (flowScore >= 0.3) return "flow_mid";
  return "flow_low";
}

function signalResult(signal: RecipeSignal): "win" | "loss" | "flat" | "pending" {
  const outcome = token(signal.outcome, "");
  if (["win", "winner", "tp", "profit"].some((part) => outcome.includes(part))) return "win";
  if (["loss", "loser", "sl", "stop"].some((part) => outcome.includes(part))) return "loss";
  if (["breakeven", "flat"].some((part) => outcome.includes(part))) return "flat";
  if (typeof signal.pnl_dollars === "number" && Number.isFinite(signal.pnl_dollars)) {
    if (signal.pnl_dollars > 0) return "win";
    if (signal.pnl_dollars < 0) return "loss";
    return "flat";
  }
  return "pending";
}

export function recipeStatus(n: number): RecipeMatrixRow["status"] {
  if (n >= 50) return "manual_review";
  if (n >= 30) return "discussion";
  return "collect";
}

export function recipeRobustness(symbols: readonly string[], sessions: readonly string[]): RecipeMatrixRow["robustness"] {
  if (symbols.length >= 3 && sessions.length >= 2) return "broad";
  if (symbols.length >= 2 || sessions.length >= 2) return "mixed";
  return "fragile";
}

function agentFrequencyStatus(n: number, share: number | null): AgentSignalFrequencyRow["status"] {
  if (share !== null && share < 0.1) return "starved";
  if (n < 30) return "thin_sample";
  return "collecting";
}

function agentFrequencyRecommendation(agent: string, status: AgentSignalFrequencyRow["status"]): string {
  if (agent === "fbd" && status === "starved") return "Add near-miss trap/reclaim outcome tracking before loosening FBD gates.";
  if (agent === "mr" && status === "starved") return "Add blocked-extension outcome tracking before loosening MR hard gates.";
  if (status === "thin_sample") return "Keep collecting; sample is too small for promotion or gate changes.";
  return "Enough sends to review expectancy by recipe, symbol, and session.";
}

export function buildAgentSignalFrequencyRows(
  signals: readonly RecipeSignal[],
  agents: readonly string[] = ["orb", "fbd", "mr"],
): AgentSignalFrequencyRow[] {
  const normalizedAgents = agents.map((agent) => agent.toLowerCase());
  const agentSet = new Set(normalizedAgents);
  const counts = new Map<string, { n: number; sessions: Set<string> }>();
  for (const agent of normalizedAgents) counts.set(agent, { n: 0, sessions: new Set<string>() });

  for (const signal of signals) {
    const agent = token(signal.agent, "");
    if (!agentSet.has(agent)) continue;
    const bucket = counts.get(agent);
    if (!bucket) continue;
    bucket.n += 1;
    bucket.sessions.add(token(signal.session, "session_missing"));
  }

  const total = Array.from(counts.values()).reduce((sum, row) => sum + row.n, 0);

  return normalizedAgents.map((agent) => {
    const bucket = counts.get(agent) ?? { n: 0, sessions: new Set<string>() };
    const share = total > 0 ? bucket.n / total : null;
    const status = agentFrequencyStatus(bucket.n, share);
    return {
      agent,
      n: bucket.n,
      share,
      sessions: Array.from(bucket.sessions).sort(),
      status,
      recommendation: agentFrequencyRecommendation(agent, status),
    };
  });
}

export function buildRecipeMatrixRows(signals: readonly RecipeSignal[], limit = 12): RecipeMatrixRow[] {
  const buckets = new Map<string, RecipeBucket>();

  for (const signal of signals) {
    const result = signalResult(signal);
    if (result === "pending" || result === "flat") continue;
    const agent = token(signal.agent, "agent_missing");
    const symbol = token(signal.symbol, "symbol_missing").toUpperCase();
    const session = token(signal.session, "session_missing");
    const hmm = token(signal.hmm_regime, "hmm_missing");
    const kronos = token(signal.kronos_direction, "kronos_missing");
    const sma = signal.sma50_aligned === null || signal.sma50_aligned === undefined ? "sma_missing" : `sma_${token(signal.sma50_aligned)}`;
    const flow = flowToken(signal);
    const fbe = token(signal.forecast_stable_state, "fbe_missing");
    const recipe = `${agent} · ${hmm} · kronos_${kronos} · ${sma} · ${flow} · ${fbe}`;

    const existing = buckets.get(recipe) ?? {
      recipe,
      agent,
      n: 0,
      wins: 0,
      losses: 0,
      winRate: null,
      totalPnl: 0,
      avgPnl: null,
      symbols: [],
      sessions: [],
      robustness: "fragile" as const,
      status: "collect" as const,
      symbolSet: new Set<string>(),
      sessionSet: new Set<string>(),
    };
    existing.n += 1;
    existing.symbolSet.add(symbol);
    existing.sessionSet.add(session);
    if (result === "win") existing.wins += 1;
    if (result === "loss") existing.losses += 1;
    if (typeof signal.pnl_dollars === "number" && Number.isFinite(signal.pnl_dollars)) {
      existing.totalPnl += signal.pnl_dollars;
    }
    buckets.set(recipe, existing);
  }

  return Array.from(buckets.values())
    .map(({ symbolSet, sessionSet, ...row }) => {
      const symbols = Array.from(symbolSet).sort();
      const sessions = Array.from(sessionSet).sort();
      return {
        ...row,
        symbols,
        sessions,
        robustness: recipeRobustness(symbols, sessions),
        winRate: row.wins + row.losses > 0 ? row.wins / (row.wins + row.losses) : null,
        avgPnl: row.n > 0 ? row.totalPnl / row.n : null,
        status: recipeStatus(row.n),
      };
    })
    .sort((a, b) => b.n - a.n || b.totalPnl - a.totalPnl || a.recipe.localeCompare(b.recipe))
    .slice(0, limit);
}
